import { useCallback, useEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import type {
    SimulatorAppliedFilters,
    SimulatorCategoryApplyFilter,
    SimulatorFilterCategory,
} from './SimulatorFilterPanel.types';
import type { SimulatorFilterOption } from './SimulatorFilterCategoryPanel';
import type { SimulatorShowOnlyState } from './simulatorFilter.helpers';
import {
    runCategoryOptionToggle,
    runCategorySelectAllChange,
    runCategorySelectionChange,
    runFilterPanelApply,
} from './simulatorFilterPanelApply.handlers';

type UseSimulatorFilterPanelApplyArgs = {
    isOpen: boolean;
    appliedCategoryTotalCounts: Record<string, number>;
    categorySelections: Record<string, string[]>;
    categorySelectAll: Record<string, boolean>;
    draftShowOnly: SimulatorShowOnlyState;
    defaultShowOnly: SimulatorShowOnlyState;
    selectedCategory: SimulatorFilterCategory | null;
    selectedCategoryOptions: SimulatorFilterOption[];
    fetchedCategoryTotalCount: number;
    debouncedCategorySearch: string;
    onApply?: (filters: SimulatorAppliedFilters) => void | Promise<boolean>;
    setCategorySelections: Dispatch<SetStateAction<Record<string, string[]>>>;
    setCategorySelectAll: Dispatch<SetStateAction<Record<string, boolean>>>;
    setDraftShowOnly: Dispatch<SetStateAction<SimulatorShowOnlyState>>;
    setSelectedCategoryKey: Dispatch<SetStateAction<string | null>>;
    setIsEditedActionsOpen: Dispatch<SetStateAction<boolean>>;
};

function normalizeFooterActionId(id: string): string {
    return id.trim().toLowerCase().replace(/-/g, '_');
}

export function useSimulatorFilterPanelApply({
    isOpen,
    appliedCategoryTotalCounts,
    categorySelections,
    categorySelectAll,
    draftShowOnly,
    defaultShowOnly,
    selectedCategory,
    selectedCategoryOptions,
    fetchedCategoryTotalCount,
    debouncedCategorySearch,
    onApply,
    setCategorySelections,
    setCategorySelectAll,
    setDraftShowOnly,
    setSelectedCategoryKey,
    setIsEditedActionsOpen,
}: UseSimulatorFilterPanelApplyArgs) {
    const [isApplyingFilters, setIsApplyingFilters] = useState(false);
    const [categorySelectAllPreviewOptions, setCategorySelectAllPreviewOptions] = useState<
        Record<string, SimulatorFilterOption[]>
    >({});
    const [categoryUsedSelectAll, setCategoryUsedSelectAll] = useState<Record<string, boolean>>({});
    const [categoryDraftApplyFilters, setCategoryDraftApplyFilters] = useState<
        Record<string, SimulatorCategoryApplyFilter>
    >({});
    const pendingClearUsedSelectAllRef = useRef<string | null>(null);

    useEffect(() => {
        if (!isOpen) return;
        setCategoryUsedSelectAll({});
        setCategoryDraftApplyFilters({});
    }, [isOpen]);

    const syncSelectAllPreviewForCategory = useCallback(
        (categoryKey: string, options: SimulatorFilterOption[]) => {
            if (!options.length) return;
            setCategorySelectAllPreviewOptions((current) => ({
                ...current,
                [categoryKey]: options,
            }));
        },
        [],
    );

    useEffect(() => {
        if (!selectedCategory || !categorySelectAll[selectedCategory.key]) return;
        syncSelectAllPreviewForCategory(selectedCategory.key, selectedCategoryOptions);
    }, [
        categorySelectAll,
        selectedCategory,
        selectedCategoryOptions,
        syncSelectAllPreviewForCategory,
    ]);

    const handleCategorySelectAllChange = useCallback(
        (checked: boolean) => {
            runCategorySelectAllChange({
                checked,
                selectedCategory,
                debouncedCategorySearch,
                categorySelections,
                selectedCategoryOptions,
                pendingClearUsedSelectAllRef,
                setCategorySelectAll,
                setCategorySelectAllPreviewOptions,
                setCategoryUsedSelectAll,
                setCategoryDraftApplyFilters,
                setCategorySelections,
                syncSelectAllPreviewForCategory,
            });
        },
        [
            categorySelections,
            debouncedCategorySearch,
            selectedCategory,
            selectedCategoryOptions,
            setCategorySelectAll,
            setCategorySelections,
            syncSelectAllPreviewForCategory,
        ],
    );

    const handleFooterAction = useCallback(
        async (actionId: string) => {
            const normalizedActionId = normalizeFooterActionId(actionId);

            if (normalizedActionId === 'reset') {
                setSelectedCategoryKey(null);
                setIsEditedActionsOpen(false);
                setCategorySelections({});
                setCategorySelectAll({});
                setCategoryUsedSelectAll({});
                setCategoryDraftApplyFilters({});
                setCategorySelectAllPreviewOptions({});
                setDraftShowOnly(defaultShowOnly);
                return;
            }

            if (normalizedActionId !== 'apply') return;

            setIsApplyingFilters(true);
            try {
                await runFilterPanelApply({
                    appliedCategoryTotalCounts,
                    selectedCategory,
                    fetchedCategoryTotalCount,
                    categorySelections,
                    categorySelectAll,
                    categoryUsedSelectAll,
                    categoryDraftApplyFilters,
                    categorySelectAllPreviewOptions,
                    draftShowOnly,
                    onApply,
                });
            } finally {
                setIsApplyingFilters(false);
            }
        },
        [
            appliedCategoryTotalCounts,
            defaultShowOnly,
            draftShowOnly,
            fetchedCategoryTotalCount,
            categorySelectAll,
            categoryUsedSelectAll,
            categoryDraftApplyFilters,
            categorySelectAllPreviewOptions,
            categorySelections,
            onApply,
            selectedCategory,
            setCategorySelectAll,
            setCategorySelections,
            setDraftShowOnly,
            setIsEditedActionsOpen,
            setSelectedCategoryKey,
        ],
    );

    const handleCategoryOptionToggle = useCallback(
        (categoryKey: string, optionId: string, checked: boolean, fromSelectAll: boolean) => {
            runCategoryOptionToggle(
                categoryKey,
                optionId,
                checked,
                fromSelectAll,
                categoryUsedSelectAll,
                pendingClearUsedSelectAllRef,
                setCategoryUsedSelectAll,
                setCategoryDraftApplyFilters,
            );
        },
        [categoryUsedSelectAll],
    );

    const handleCategorySelectionChange = useCallback(
        (categoryKey: string, ids: string[], fromSelectAll: boolean) => {
            runCategorySelectionChange(
                categoryKey,
                ids,
                fromSelectAll,
                categoryUsedSelectAll,
                pendingClearUsedSelectAllRef,
                setCategoryUsedSelectAll,
                setCategoryDraftApplyFilters,
                setCategorySelectAll,
                setCategorySelections,
            );
        },
        [categoryUsedSelectAll, setCategorySelectAll, setCategorySelections],
    );

    return {
        handleCategorySelectAllChange,
        handleCategoryOptionToggle,
        handleCategorySelectionChange,
        handleFooterAction,
        isApplyingFilters,
    };
}
